// Service de la feature "chiffrage" : 1:1 avec les routes core /bdc/chiffrage* (proxy vers le module
// BDC) + l'application d'un code cote acheteur. Meme conventions que les autres services (token Bearer
// depuis localStorage, baseUrl process.env.REACT_APP_API_URL, reponses back {status, data|message}).

import type {
  Chiffrage,
  ChiffrageCouple,
  ChiffrageCreatePayload,
  ChiffrageUpdatePayload,
  ApplyChiffrageCodeResult,
} from '../pages/Chiffrage/chiffrageTypes';

class ChiffrageService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
  }

  // Appel authentifie qui deballe l'enveloppe {status, data} et remonte le message d'erreur du back.
  // Utilise par toutes les methodes du perimetre prestataire (le core scope par id_entite).
  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Aucun token d\'authentification trouvé');
    }

    const response = await fetch(`${this.baseUrl}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok || body?.status === 'error') {
      throw new Error(body?.message || `Erreur HTTP: ${response.status}`);
    }

    return body?.data as T;
  }

  // Couples (lot technique x departement) ou le prestataire connecte est titulaire.
  async getCouples(): Promise<ChiffrageCouple[]> {
    return (await this.request<ChiffrageCouple[]>('/bdc/chiffrage/couples')) || [];
  }

  // Tous les chiffrages du prestataire connecte (tous statuts).
  async listChiffrages(): Promise<Chiffrage[]> {
    return (await this.request<Chiffrage[]>('/bdc/chiffrage')) || [];
  }

  // Detail d'un chiffrage (avec ses articles).
  async getChiffrage(id: number): Promise<Chiffrage> {
    return this.request<Chiffrage>(`/bdc/chiffrage/${id}`);
  }

  // Cree un brouillon. `id_entite` est injecte par le core, jamais transmis ici.
  async createChiffrage(payload: ChiffrageCreatePayload): Promise<Chiffrage> {
    return this.request<Chiffrage>('/bdc/chiffrage', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Met a jour un brouillon (le back refuse la maj si le statut n'est plus 'brouillon').
  async updateChiffrage(id: number, payload: ChiffrageUpdatePayload): Promise<Chiffrage> {
    return this.request<Chiffrage>(`/bdc/chiffrage/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Genere le code et passe le chiffrage en 'actif' (immuable des lors). Renvoie le chiffrage avec son code.
  async generateCode(id: number): Promise<Chiffrage> {
    return this.request<Chiffrage>(`/bdc/chiffrage/${id}/generate-code`, {
      method: 'POST',
    });
  }

  // Masque le chiffrage de la liste du prestataire (le statut reste 'actif', le code reste valide).
  async hideChiffrage(id: number): Promise<Chiffrage> {
    return this.request<Chiffrage>(`/bdc/chiffrage/${id}/hide`, {
      method: 'POST',
    });
  }

  // Supprime un chiffrage (le back n'autorise la suppression que sur un brouillon).
  async deleteChiffrage(id: number): Promise<void> {
    await this.request<unknown>(`/bdc/chiffrage/${id}`, {
      method: 'DELETE',
    });
  }

  // Applique un code sur une operation BC cote acheteur : le back valide (lot/departement, titularite,
  // cascade de rang) puis pre-remplit le catalogue de l'operation. Ne consomme pas le code.
  // L'enveloppe est renvoyee telle quelle (succes ET echec metier portent un message), sans throw sur
  // un echec metier : c'est au consommateur d'afficher le message et de recharger les articles.
  async applyCode(operationId: number, code: string): Promise<ApplyChiffrageCodeResult> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Aucun token d\'authentification trouvé');
    }

    const response = await fetch(`${this.baseUrl}/bdc/operation/${operationId}/apply-chiffrage-code`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    const body = await response.json().catch(() => ({}));

    return {
      status: body?.status === 'success' ? 'success' : 'error',
      message: body?.message,
      data: body?.data,
    };
  }
}

export const chiffrageService = new ChiffrageService();
export default chiffrageService;
